<template>
  <div>
    <md-dialog :md-active.sync="pdfDialog">
      <md-dialog-title>PDF Preview</md-dialog-title>
      <md-content style="overflow-y: scroll !important;" class="md-scrollbar">
        <MaterialPDF :pdf="pdf" />
      </md-content>
      <md-dialog-actions>
        <md-button class="md-primary" @click="pdfDialog = false"
          >Close</md-button
        >
      </md-dialog-actions>
    </md-dialog>

    <md-dialog :md-active.sync="historyDialog.show" class="history-dialog">
      <md-dialog-title>
        Edit History Log
        <span v-if="historyDialog.material" class="history-dialog__subtitle">
          Sponsorship #{{ historyDialog.material.id }}
        </span>
      </md-dialog-title>
      <md-dialog-content class="history-dialog__content">
        <AppSpinner v-if="historyDialog.loading" :loading="historyDialog.loading">
          <template v-slot:default>
            <div class="history-dialog__status">Loading history...</div>
          </template>
        </AppSpinner>

        <div v-else-if="historyDialog.error" class="history-dialog__status history-dialog__status--error">
          {{ historyDialog.error }}
        </div>

        <div v-else-if="historyDialog.entries.length === 0" class="history-dialog__status">
          No edit history found for this sponsorship.
        </div>

        <ol v-else class="history-timeline">
          <li
            v-for="entry in historyDialog.entries"
            :key="entry.id"
            class="history-timeline__item"
          >
            <div class="history-timeline__marker">
              <div class="history-timeline__dot"></div>
            </div>
            <div class="history-timeline__content">
              <div class="history-timeline__meta">
                <span class="history-timeline__time">{{ formatHistoryTimestamp(entry.created_at) }}</span>
                <span class="history-timeline__separator">•</span>
                <span class="history-timeline__user">{{ entry.user_name || 'Unknown user' }}</span>
              </div>
              <div class="history-timeline__message">
                {{ entry.message || fallbackHistoryMessage(entry) }}
              </div>
              <div class="history-timeline__change">
                <div class="history-timeline__field-row">
                  <span class="history-timeline__field-label">Field</span>
                  <span class="history-timeline__field">{{ formatHistoryField(entry.column) }}</span>
                </div>
                <div class="history-timeline__values">
                  <span class="history-timeline__value-label">From</span>
                  <span class="history-timeline__value">{{ formatHistoryValue(entry.from) }}</span>
                  <span class="history-timeline__arrow">-></span>
                  <span class="history-timeline__value-label">To</span>
                  <span class="history-timeline__value">{{ formatHistoryValue(entry.to) }}</span>
                </div>
              </div>
            </div>
          </li>
        </ol>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button class="md-primary" @click="closeHistoryDialog">Close</md-button>
      </md-dialog-actions>
    </md-dialog>

    <AppSpinner v-if="loadingSpinner" :loading="loadingSpinner" />

    <transition mode="out-in" name="fade">
      <div class="material-table" v-if="!loadingSpinner">
        <template v-if="Array.isArray(materials) && materials.length === 0">
          <div class="empty-table-message">
            Nothing found.
          </div>
        </template>
        <template v-else>
          <div class="sticky_header">
            <div class="singleMaterialRow__btns md-layout md-raised">
              <div
                class="md-layout-item md-xlarge-size-5 md-large-size-5 md-medium-size-10 md-small-size-20 singleInstallationRow__check-btn check-btn main-checkbox"
              >
                <input
                  type="checkbox"
                  class="input -check-box"
                  v-model="isAllChecked"
                />
                <!--<md-icon>arrow_drop_down</md-icon>-->
              </div>
              <div
                class="md-layout-item md-xlarge-size-20 md-large-size-20 md-medium-size-45 md-small-size-90"
              >
                <AppInputSelectDropdown
                  :placeholder="'Select material'"
                  class="materials-select__field"
                  v-model="currentMaterialID"
                >
                  <option
                    v-for="(material, index) in materialsForDropdown"
                    :key="index"
                    :value="material.id"
                  >
                    {{ material.name }}
                  </option>
                </AppInputSelectDropdown>
              </div>

              <div
                class="md-layout-item md-xlarge-size-20 md-large-size-20 md-medium-size-45 md-small-size-90"
              >
                <md-button
                  class="md-primary md-raised send-to-queue-btn"
                  @click="sendToQueueMaterialsDropdown()"
                  >Set materials
                </md-button>
              </div>

              <div
                class="md-layout-item md-xlarge-size-20 md-large-size-20 md-medium-size-45 md-small-size-90"
              >
                <AppInputSelectDropdown
                  v-if="orderType === 'signage'"
                  :placeholder="'Select status'"
                  class="materials-select__field"
                  v-model="currentStatusID"
                >
                  <option
                    v-for="(status, index) in signageOrderStatuses"
                    :key="index"
                    :value="status.id"
                  >
                    {{ status.name }}
                  </option>
                </AppInputSelectDropdown>
                <AppInputSelectDropdown
                  v-if="orderType === 'sponsorship'"
                  :placeholder="'Select status'"
                  class="materials-select__field"
                  v-model="currentStatusID"
                >
                  <option
                    v-for="(status, index) in sponsorshipStatuses"
                    :key="index"
                    :value="status.sponsorship_status_id"
                  >
                    {{ displaySponsorshipStatusLabel(status) }}
                  </option>
                </AppInputSelectDropdown>
              </div>

              <div
                class="md-layout-item md-xlarge-size-20 md-large-size-20 md-medium-size-45 md-small-size-90"
              >
                <md-button
                  class="md-primary md-raised send-to-queue-btn"
                  @click="changeStatuses()"
                  >Set status
                </md-button>
              </div>
            </div>

            <div class="singleMaterialRow__header md-layout md-raised">
              <h2
                class="md-layout-item md-xlarge-size-5 md-large-size-5 md-medium-size-5 md-small-size-5"
              ></h2>
              <h2
                class="md-layout-item md-xlarge-size-10 md-large-size-10 md-medium-size-10 md-small-size-15 material-hdrs"
                @click="alphabeticalOrder('id')"
              >
                <md-tooltip md-direction="top">Click to sort</md-tooltip>
                Order ID
              </h2>
              <h2
                class="md-layout-item md-xlarge-size-10 md-large-size-10 md-medium-size-10 md-small-size-15 material-hdrs"
              >
                Advertiser Name
              </h2>
              <h2
                class="md-layout-item md-xlarge-size-15 md-large-size-15 md-medium-size-20 md-small-size-25 material-hdrs"
                @click="alphabeticalOrder('description')"
              >
                <md-tooltip md-direction="top">Click to sort</md-tooltip>
                AdCode
              </h2>
              <h2
                class="md-layout-item md-xlarge-size-15 md-large-size-15 md-medium-size-15 md-small-size-15 material-hdrs"
                @click="alphabeticalOrder('materialName')"
              >
                <md-tooltip md-direction="top">Click to sort</md-tooltip>
                Material
              </h2>
              <h2
                class="md-layout-item md-xlarge-size-15 md-large-size-15 md-medium-size-15 md-small-size-15 material-hdrs"
                @click="alphabeticalOrder('statusName')"
              >
                <md-tooltip md-direction="top">Click to sort</md-tooltip>
                Status
              </h2>
              <h2
                class="md-layout-item md-xlarge-size-5 md-large-size-5 md-medium-size-10 material-hdrs"
                @click="alphabeticalOrder('size')"
              >
                <md-tooltip md-direction="top">Click to sort</md-tooltip>
                Size
              </h2>
              <h2
                class="md-layout-item md-xlarge-size-10 md-large-size-10 md-medium-size-10 material-hdrs"
                @click="alphabeticalOrder('quantity')"
              >
                <md-tooltip md-direction="top">Click to sort</md-tooltip>
                Quantity
              </h2>
              <h2
                class="md-layout-item md-xlarge-size-15 md-large-size-15 md-medium-size-20 md-small-size-20 material-hdrs"
              >
                History
              </h2>
            </div>
          </div>

          <div
            v-for="(material, index) in sortedMaterials.filter((m) => !!m)"
            :key="index + '_singleClass'"
          >
            <div class="singleMaterialRow md-raised md-layout">
              <div
                class="md-layout-item md-xlarge-size-5 md-large-size-5 md-medium-size-5 md-small-size-5"
              >
                <div class="md-layout md-layout-item">
                  <div class="singleMaterialRow__check-btn check-btn">
                    <label>
                      <input
                        type="checkbox"
                        class="input-check-box"
                        v-model="material.is_checked"
                      />
                    </label>
                  </div>
                </div>
              </div>
              <!--                        {{ material.artworkThumbnail }}-->
              <!--                      :href="material.artworkThumbnail"-->
              <div
                class="singleMaterialRow__data special-labels md-x large-size-10 md-layout-item md-large-size-10 md-medium-size-10 md-small-size-15"
              >
                <a
                  @click="selectMaterial(material)"
                  style="color: #414a97; font-weight: bold"
                  class="blue-color"
                  target="_blank"
                  >{{ material.id }}</a
                >
              </div>
              <div
                class="singleMaterialRow__data blue-color special-labels md-xlarge-size-10 md-layout-item md-large-size-10 md-medium-size-10 md-small-size-15"
              >
                {{ orderType === "sponsorship" ? material.companyName : "IMC" }}
              </div>

              <div
                class="singleMaterialRow__data blue-color md-layout-item md-xlarge-size-15 md-large-size-15 md-medium-size-20 md-small-size-30"
              >
                {{ material.spaceDescription }}
              </div>

              <div
                class="singleMaterialRow__data md-layout-item md-xlarge-size-15 md-large-size-15 md-medium-size-15 md-small-size-15"
              >
                <!--{{installation.material_name}}-->

                <AppInputSelectDropdown
                  :placeholder="'Select material'"
                  class="materials-select__field"
                  v-model="material.materialId"
                  @change="onChangeSingleMaterialDropdown(material)"
                >
                  <option
                    v-for="(material, index) in materialsForDropdown"
                    :key="index"
                    :value="material.id"
                  >
                    {{ material.name }}
                  </option>
                </AppInputSelectDropdown>
              </div>

              <div
                class="singleMaterialRow__data status_name md-layout-item md-xlarge-size-15 md-large-size-15 md-medium-size-15 md-small-size-15"
              >
                <AppInputSelectDropdown
                  v-show="orderType === 'signage'"
                  :placeholder="'Select status'"
                  class="materials-select__field"
                  v-model="material.status"
                  @change="onChangeSingleStatusDropdown(material)"
                >
                  <option
                    v-for="(status, index) in signageOrderStatuses"
                    :key="index + 'signageOrderStatuses'"
                    :value="status.id"
                  >
                    {{ status.name }}
                  </option>
                </AppInputSelectDropdown>

                <AppInputSelectDropdown
                  v-show="orderType === 'sponsorship'"
                  :placeholder="'Select status'"
                  class="materials-select__field"
                  v-model="material.status"
                  @change="onChangeSingleStatusDropdown(material)"
                >
                  <option
                    v-for="(status, index) in sponsorshipStatuses"
                    :key="index + 'sponsorshipStatuses'"
                    :value="status.sponsorship_status_id"
                  >
                    {{ displaySponsorshipStatusLabel(status) }}
                  </option>
                </AppInputSelectDropdown>
              </div>

              <div
                class="singleMaterialRow__data special-labels md-layout-item md-xlarge-size-5 md-large-size-5 md-medium-size-10 mobile-remove"
              >
                {{ material.size }}
              </div>
              <div
                class="singleMaterialRow__data special-labels md-layout-item md-xlarge-size-10 md-large-size-10 md-medium-size-10"
              >
                {{ material.quantity }}
              </div>
              <div
                class="singleMaterialRow__data singleMaterialRow__history md-layout-item md-xlarge-size-15 md-large-size-15 md-medium-size-20 md-small-size-20"
              >
                <md-button
                  class="md-icon-button md-primary history-button"
                  @click="openHistoryDialog(material)"
                  aria-label="View history"
                >
                  <md-icon>history</md-icon>
                  <md-tooltip md-direction="top">View history</md-tooltip>
                </md-button>
              </div>
            </div>
          </div>
        </template>
      </div>
    </transition>

    <md-snackbar
      md-position="center"
      :md-active.sync="msgSnackbar.show"
      :md-duration="msgSnackbar.duration"
      md-persistent
    >
      <span>{{ msgSnackbar.message }}</span>
      <md-button class="md-primary" @click="msgSnackbar.show = false"
        >Got it</md-button
      >
    </md-snackbar>

    <md-snackbar
      md-position="center"
      :md-active.sync="snackbar.show"
      :md-duration="snackbar.duration"
      md-persistent
    >
      <span>{{ snackbar.message }}</span>
      <md-button class="md-primary" @click="undoStatusUpdate">Undo</md-button>
    </md-snackbar>

    <md-dialog :md-active.sync="previewDialog">
      <md-dialog-title>Thumbnail Preview</md-dialog-title>
      <md-dialog-content
        style="display: flex; flex-direction: row; align-items: flex-start; gap: 16px;"
      >
        <div v-if="selectedMaterial.artworkThumbnail">
          <img
            :src="selectedMaterial.artworkThumbnail"
            alt="Front thumbnail"
            style="max-width: 180px; max-height: 180px; border: 1px solid #ccc;"
          />
          <div style="text-align: center; font-size: 0.9em; margin-top: 4px;">
            Front side
          </div>
        </div>
        <div v-if="selectedMaterial.backsideArtworkThumbnail">
          <img
            :src="selectedMaterial.backsideArtworkThumbnail"
            alt="Backside thumbnail"
            style="max-width: 180px; max-height: 180px; border: 1px solid #ccc;"
          />
          <div style="text-align: center; font-size: 0.9em; margin-top: 4px;">
            Back side
          </div>
        </div>
      </md-dialog-content>
    </md-dialog>

    <md-dialog :md-active.sync="showPrintProblemDialog">
      <md-dialog-title>Preflight Problem Note</md-dialog-title>
      <md-dialog-content>
        <md-field>
          <label>Describe the preflight problem</label>
          <md-textarea v-model="printProblemNote" />
        </md-field>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button class="md-primary" @click="submitPrintProblemNote"
          >Submit</md-button
        >
        <md-button @click="cancelPrintProblemNote">Cancel</md-button>
      </md-dialog-actions>
    </md-dialog>
  </div>
</template>

<script>
import AppInputSelectDropdown from "@/components/Base/AppInputSelectDropdown";
import { InitializeOrderMixin } from "@/mixins/InitializeOrderMixin";
import MaterialPDF from "@/components/MaterialDashboard/MaterialPDF.vue";
import AppSpinner from "@/components/Base/AppSpinner.vue";
import Cookie from "js-cookie";
import { sponsorshipStatuses } from "@/constants";

export default {
  name: "MaterialDashboardTable",
  mixins: [InitializeOrderMixin],
  props: {
    materialsProp: {
      type: Array,
    },
    searchStr: {
      type: String,
    },
    orderTypeProp: {
      type: String,
      default: "sponsorship",
    },
  },

  data: function() {
    return {
      previewDialog: false,
      selectedMaterial: {},
      showPrintProblemDialog: false,
      printProblemNote: "",
      pendingProblemStatusMaterials: [],
      fallbackImage:
        document.location.origin +
        require("@/assets/img/empty_state_image.png"),
      currentMaterialID: "",
      currentStatusID: "",
      isAllChecked: false,
      loadingSpinner: true,
      snackbar: {
        show: false,
        message: "Last chance to undo the changes?",
        duration: 4000,
      },
      msgSnackbar: {
        show: false,
        message: "",
        duration: 4000,
      },
      undo: false,
      materialsForDropdown: [],
      sponsorshipStatuses: [],
      signageOrderStatuses: [],
      materials: [],
      orderType: "",
      pdfDialog: false,
      pdf: "",
      marketId: Cookie.get("season_market"),
      sortField: "",
      sortDirection: "asc",
      historyDialog: {
        show: false,
        loading: false,
        error: "",
        material: null,
        entries: [],
      },
    };
  },
  components: {
    AppSpinner,
    AppInputSelectDropdown,
    MaterialPDF,
  },
  computed: {
    sortedMaterials() {
      if (!Array.isArray(this.materials)) return [];
      if (!this.sortField) return this.materials;
      // Copy array to avoid mutating original
      let arr = [...this.materials];
      arr.sort((a, b) => {
        let aValue = a[this.sortField];
        let bValue = b[this.sortField];

        if (aValue == null) aValue = "";
        if (bValue == null) bValue = "";

        if (!isNaN(Number(aValue)) && !isNaN(Number(bValue))) {
          aValue = Number(aValue);
          bValue = Number(bValue);
        }

        if (aValue < bValue) return this.sortDirection === "asc" ? -1 : 1;
        if (aValue > bValue) return this.sortDirection === "asc" ? 1 : -1;
        return 0;
      });
      return arr;
    },
  },

  methods: {
    async openHistoryDialog(material) {
      if (!material || !material.id) {
        this.showSnackbarMsg("This row does not have a sponsorship id.");
        return;
      }

      if (!["sponsorship", "free-standing"].includes(material.mediaType)) {
        this.showSnackbarMsg("Edit history is available only for sponsorship rows.");
        return;
      }

      this.historyDialog.show = true;
      this.historyDialog.loading = true;
      this.historyDialog.error = "";
      this.historyDialog.material = material;
      this.historyDialog.entries = [];

      try {
        const response = await this.$axios.get(`/sponsorships/${material.id}/history`);
        const entries = Array.isArray(response.data) ? response.data : [];

        this.historyDialog.entries = [...entries].sort((left, right) => {
          const leftValue = left && left.created_at ? left.created_at : "";
          const rightValue = right && right.created_at ? right.created_at : "";

          if (leftValue < rightValue) return 1;
          if (leftValue > rightValue) return -1;
          return 0;
        });
      } catch (err) {
        this.historyDialog.error =
          err && err.response && err.response.data && err.response.data.error
            ? err.response.data.error
            : "We couldn't load the edit history.";
      } finally {
        this.historyDialog.loading = false;
      }
    },
    closeHistoryDialog() {
      this.historyDialog.show = false;
      this.historyDialog.loading = false;
      this.historyDialog.error = "";
      this.historyDialog.material = null;
      this.historyDialog.entries = [];
    },
    formatHistoryTimestamp(value) {
      if (!value) {
        return "Unknown time";
      }

      const parsedValue = new Date(String(value).replace(" ", "T"));
      if (Number.isNaN(parsedValue.getTime())) {
        return value;
      }

      return parsedValue.toLocaleString();
    },
    formatHistoryField(value) {
      if (!value) {
        return "Unknown field";
      }

      return String(value)
        .split("_")
        .join(" ");
    },
    formatHistoryValue(value) {
      if (value === null || value === undefined || value === "") {
        return "Empty";
      }

      return String(value);
    },
    fallbackHistoryMessage(entry) {
      const fromValue = this.formatHistoryValue(entry && entry.from);
      const toValue = this.formatHistoryValue(entry && entry.to);
      return `Changed ${this.formatHistoryField(entry && entry.column)} from ${fromValue} to ${toValue}.`;
    },
    selectMaterial(material) {
      this.selectedMaterial = { ...material }; // force reactivity
      console.log("selectMaterial called, emitting:", material);
      this.$emit("selectedOrder", material); // Emit selected order to parent
      console.log("Selected material:", this.selectedMaterial);
      console.log(
        "Backside thumbnail URL:",
        this.selectedMaterial.backside_artwork_url_thumbnail,
      );
      this.previewDialog = true;
    },
    changeStatuses() {
      if (this.currentStatusID.length > "0") {
        if (this.isSponsorshipProblemStatus(this.currentStatusID)) {
          const pendingMaterials = [];
          for (let i = 0; i < this.materials.length; i++) {
            if (this.materials[i].is_checked) {
              this.materials[i].status = this.currentStatusID;
              pendingMaterials.push(this.materials[i]);
            }
          }
          if (pendingMaterials.length > 0) {
            this.promptPrintProblemNote(pendingMaterials);
          }
          return;
        }
        for (let i = 0; i < this.materials.length; i++) {
          if (this.materials[i].is_checked) {
            this.materials[i].status = this.currentStatusID;
            this.onChangeSingleStatusDropdown(this.materials[i]);
          }
        }
      } else {
        this.showSnackbarMsg(
          "You must change the main material so you can send to queue!",
        );
      }
    },

    onChangeSingleStatusDropdown(material) {
      if (this.isSponsorshipProblemStatus(material.status)) {
        this.promptPrintProblemNote([material]);
        return;
      }
      let payload = {};

      if (material.mediaType === "signage") {
        payload = {
          status_id: material.status,
        };
      } else {
        payload = {
          sponsorship_status: material.status,
        };
      }

      material
        .updateOrder(payload)
        .then(() => {
          material._lastStatus = material.status;
          this.$emit("getAllOrders");

          this.$emit("searchInput", this.searchStr);
          this.showSnackbarMsg("Status was changed !");
        })
        .catch((err) => {
          console.log(err);
          this.showSnackbarMsg(err.message);
        });
    },
    updateOrders(orders, orderType = "sponsorship") {
      this.orderType = orderType;
      this.materials = (orders || [])
        .filter((m) => !!m)
        .map((m) => {
          if (m.is_checked === undefined) m.is_checked = false;
          if (m._lastStatus === undefined) m._lastStatus = m.status;
          return m;
        });
    },

    printThisOrder(material) {
      let mediaType = "";

      if (material.mediaType) {
        mediaType = "sponsorships";
      } else {
        mediaType = "signage-orders";
      }

      this.$axios
        .get(`/${mediaType}/${material.id}/print-ticket`)
        .then((res) => {
          // this.pdfDialog = true;
          // this.pdf = res.data;
          window.open(res.data, "_blank");

          //                material.statusName = 'Queued for production';
        })
        .catch((err) => {
          this.$emit("printFailed", err.message);
        });
    },
    alphabeticalOrder(fieldToReorder) {
      // Toggle sort direction if same field, otherwise default to ascending
      if (this.sortField === fieldToReorder) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      } else {
        this.sortField = fieldToReorder;
        this.sortDirection = "asc";
      }
      // No need to sort materials array here; computed property handles sorting
    },
    isSponsorshipProblemStatus(statusId) {
      return (
        this.orderType === "sponsorship" &&
        Number(statusId) === sponsorshipStatuses.PROBLEM
      );
    },
    /** FE-only label; API still uses status id 31 (In Staging). */
    displaySponsorshipStatusLabel(status) {
      return status.sponsorship_status_name;
    },
    promptPrintProblemNote(materials) {
      this.pendingProblemStatusMaterials = materials.map((material) => ({
        material,
        previousStatus:
          material._lastStatus === undefined
            ? material.status
            : material._lastStatus,
      }));
      this.printProblemNote = "";
      this.showPrintProblemDialog = true;
    },
    submitPrintProblemNote() {
      const note = this.printProblemNote;
      const pending = [...this.pendingProblemStatusMaterials];
      this.showPrintProblemDialog = false;
      this.pendingProblemStatusMaterials = [];

      pending.forEach(({ material }) => {
        const payload = {
          sponsorship_status: material.status,
          print_problem_note: note,
        };

        material
          .updateOrder(payload)
          .then(() => {
            material._lastStatus = material.status;
            this.$emit("getAllOrders");
            this.$emit("searchInput", this.searchStr);
            this.showSnackbarMsg("Status was changed !");
          })
          .catch((err) => {
            console.log(err);
            this.showSnackbarMsg(err.message);
          });
      });
    },
    cancelPrintProblemNote() {
      this.pendingProblemStatusMaterials.forEach(
        ({ material, previousStatus }) => {
          material.status = previousStatus;
        },
      );
      this.pendingProblemStatusMaterials = [];
      this.showPrintProblemDialog = false;
    },
    showSnackbarMsg(message) {
      this.msgSnackbar.message = message;
      this.msgSnackbar.show = true;
    },

    sendToQueueMaterialsDropdown() {
      if (this.currentMaterialID.length > "0") {
        for (let i = 0; i < this.materials.length; i++) {
          if (this.materials[i].is_checked) {
            this.materials[i].materialId = this.currentMaterialID;
            this.onChangeSingleMaterialDropdown(this.materials[i]);
          }
        }
      } else {
        this.showSnackbarMsg(
          "You must change the main material so you can send to queue!",
        );
      }
    },

    onChangeSingleMaterialDropdown(material) {
      material
        .setUpOrderMaterial()
        .then(() => {
          //                this.$emit('getAllOrders');

          this.$emit("searchInput", this.searchStr);
          this.showSnackbarMsg("Material was changed !");
        })
        .catch((err) => {
          console.log(err);
          this.showSnackbarMsg(err.message);
        });
    },

    getMaterials() {
      this.materialsForDropdown = this.$store.getters["materials/getMaterials"];
      console.debug("materialsForDropdown:", this.materialsForDropdown);
    },

    getSignageOrderStatuses() {
      return this.$axios.get("signage-statuses").then((res) => {
        this.signageOrderStatuses = res.data;
      });
    },

    getSponsorshipStatuses() {
      return this.$axios.get("sponsorship-statuses").then((res) => {
        this.sponsorshipStatuses = res.data;
      });
    },

    async getStatuses() {
      await this.getSignageOrderStatuses();
      await this.getSponsorshipStatuses();
    },

    showSnackBar() {
      this.undo = false;
      this.snackbar.show = true;
    },
    undoStatusUpdate() {
      this.undo = true;
      this.snackbar.show = false;
    },
    updateMarkedAsInstalled() {
      this.showSnackBar();

      setTimeout(() => {
        if (this.undo) return;

        for (let i = 0; i < this.materials.length; i++) {
          if (this.materials[i].is_checked) {
            this.materials[i].markInstalled();
          }
        }
      }, 4000);
    },

    updateMarkedAsRemoved() {
      this.showSnackBar();

      setTimeout(() => {
        if (this.undo) return;

        for (let i = 0; i < this.materials.length; i++) {
          if (this.materials[i].is_checked) {
            this.materials[i].markRemoved();
          }
        }
      }, 4000);
    },

    async loadData() {
      // try {
      await this.getMaterials();
      await this.getStatuses();
      // } catch (err) {
      //
      //     this.showSnackbar(err.message);
      // }
    },

    getAllChecked() {
      let sponsorshipId = [];
      let signageId = [];

      let sponsorshipMaterials = [];
      let signageMaterials = [];

      for (let i = 0; i < this.materials.length; i++) {
        if (this.materials[i].is_checked) {
          if (this.materials[i].mediaType === "sponsorship") {
            sponsorshipMaterials.push(this.materials[i]);
            sponsorshipId.push(this.materials[i].id);
          } else {
            signageMaterials.push(this.materials[i]);
            signageId.push(this.materials[i].id);
          }
        }
      }

      if (signageId.length > 0) {
        let signageIdsObject = {
          signage_orders: signageId,
        };

        this.$axios
          .post(`/signage-order-print-tickets`, signageIdsObject)
          .then((res) => {
            window.open(res.data, "_blank");

            for (let i = 0; i < signageMaterials.length; i++) {
              //                        signageMaterials[i].statusName = 'Queued for production';
            }
          })
          .catch((err) => {
            this.$emit("printFailed", err.message);
          });
      }

      if (sponsorshipId.length > 0) {
        let sponsorshipIdsObject = {
          sponsorships: sponsorshipId,
        };

        this.$axios
          .post(`/sponsorship-print-tickets`, sponsorshipIdsObject)
          .then((res) => {
            window.open(res.data, "_blank");

            for (let i = 0; i < sponsorshipMaterials.length; i++) {
              //                        sponsorshipMaterials[i].statusName = 'Queued for production';
            }
          })
          .catch((err) => {
            this.$emit("printFailed", err.message);
          });
      }
    },
  },

  // mounted() {
  //   let recaptchaScript = document.createElement("script");
  //   recaptchaScript.setAttribute(
  //       "src",
  //       "//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js"
  //   );
  //   // recaptchaScript.setAttribute("src", "//mozilla.github.io/pdf.js/build/pdf.js");
  //   document.head.appendChild(recaptchaScript);
  //
  // },

  created() {
    let self = this;
    self.orderType = self.orderTypeProp;
    self.loadData().then(() => {
      // Debug: print incoming materialsProp
      // Ensure every material has is_checked property and filter out invalid items, but preserve prototype
      self.materials = (self.materialsProp || [])
        .filter((m) => !!m && m.status !== 40 && m.status !== 49)
        .map((m) => {
          if (m.is_checked === undefined) m.is_checked = false;
          if (m._lastStatus === undefined) m._lastStatus = m.status;
          return m;
        });
      // Debug: print processed materials
      console.debug(
        "materialsForDropdown (created):",
        self.materialsForDropdown,
      );
      self.loadingSpinner = false;
    });
  },

  watch: {
    isAllChecked(value) {
      for (let i = 0; i < this.materials.length; i++) {
        this.materials[i].is_checked = value;
      }
      this.$forceUpdate();
    },
    materialsProp: {
      handler(newVal) {
        this.loadingSpinner = true;
        // Debug: log incoming prop and what will be saved

        // If newVal is an empty array, clear materials immediately
        if (Array.isArray(newVal) && newVal.length === 0) {
          this.materials = [];
        } else {
          let processedMaterials = Array.isArray(newVal)
            ? newVal.map((m) => {
                if (m && m.is_checked === undefined) m.is_checked = false;
                if (m && m._lastStatus === undefined) m._lastStatus = m.status;
                return m;
              })
            : [];

          this.materials = processedMaterials.filter((m) => m !== undefined);
        }
        this.orderType = this.orderTypeProp;
        this.$nextTick(() => {
          this.loadingSpinner = false;
        });
        this.$forceUpdate();
      },
      immediate: true,
      deep: false,
    },
  },
};
</script>

<style lang="scss" scoped>
//.container-mobile-installation-modal {
//    position: absolute;
//    top: 30%;
//    text-align: center;
//    left: 0;
//    outline: 9999px solid rgba(0, 0, 0, 0.5);
//    background-color: #ffffff;
//    /* margin-right: 20px; */
//}

.singleMaterialRow__header {
  text-align: center;
  background-color: white;
  font-weight: bold;
  color: #222222;
  padding-top: 10px;
  padding-bottom: 10px;
  border-bottom: 2px solid gray;
  @include respond(small) {
    font-size: 0.8rem;
  }
}


.singleMaterialRow__history {
  display: flex;
  align-items: center;
  justify-content: center;
}

.history-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  width: 36px;
  height: 36px;
  margin: 0 auto;
}

.history-button /deep/ .md-button-content {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.history-button /deep/ .md-icon {
  font-size: 20px !important;
  line-height: 1;
  margin: 0;
}

.history-dialog {
  /deep/ .md-dialog-container {
    width: calc(100vw - 48px);
    max-width: 1100px;
  }

  /deep/ .md-dialog-title {
    font-size: 1.7rem;
    line-height: 1.3;
    padding-bottom: 8px;
  }

  /deep/ .md-dialog-content {
    overflow-x: hidden;
  }
}

.history-dialog__subtitle {
  display: block;
  margin-top: 8px;
  font-size: 1.35rem;
  font-weight: 500;
  color: #475467;
}

.history-dialog__content {
  width: 100%;
  min-width: 320px;
  max-width: none;
  padding: 0;
}

.history-dialog__status {
  padding: 32px 16px;
  text-align: center;
  color: #475467;
  font-size: 1.15rem;
}

.history-dialog__status--error {
  color: #b42318;
}

.history-timeline {
  list-style: none;
  margin: 0;
  padding: 16px 24px 20px 24px;
}

.history-timeline__item {
  position: relative;
  display: flex;
  gap: 18px;
  padding: 0 0 28px;
}

.history-timeline__marker {
  position: relative;
  flex: 0 0 22px;
}

.history-timeline__dot {
  position: relative;
  z-index: 1;
  width: 14px;
  height: 14px;
  margin: 6px auto 0;
  border-radius: 50%;
  background: #414a97;
  border: 2px solid #ffffff;
  box-shadow: 0 0 0 2px #c7d7ff;
}

.history-timeline__marker::after {
  content: "";
  position: absolute;
  top: 20px;
  bottom: -28px;
  left: 50%;
  width: 2px;
  transform: translateX(-50%);
  background: #cfd8ea;
}

.history-timeline__item:last-child .history-timeline__marker::after {
  display: none;
}

.history-timeline__content {
  flex: 1;
  padding-top: 1px;
  text-align: left;
}

.history-timeline__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 1.22rem;
  color: #667085;
}

.history-timeline__user {
  font-weight: 600;
  color: #344054;
}

.history-timeline__time {
  color: #667085;
}

.history-timeline__separator {
  color: #98a2b3;
}

.history-timeline__message {
  margin-bottom: 10px;
  font-size: 1.32rem;
  line-height: 1.45;
  color: #101828;
}

.history-timeline__change {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 6px;
  font-size: 1.2rem;
}

.history-timeline__field-row {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
}

.history-timeline__field {
  font-size: 1.2rem;
  font-weight: 600;
  color: #22316e;
  text-transform: capitalize;
}

.history-timeline__values {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  color: #475467;
}

.history-timeline__value-label {
  font-size: 1.08rem;
  font-weight: 600;
  color: #667085;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.history-timeline__value {
  font-size: 1.2rem;
  color: #101828;
}

.history-timeline__arrow {
  font-weight: 700;
  color: #667085;
}

@media (max-width: 960px) {
  .history-dialog {
    /deep/ .md-dialog-container {
      width: calc(100vw - 24px);
      max-width: calc(100vw - 24px);
    }

    /deep/ .md-dialog-title {
      font-size: 1.4rem;
    }
  }

  .history-timeline__meta {
    align-items: flex-start;
    flex-direction: column;
    gap: 4px;
  }

  .history-dialog__content {
    min-width: 0;
  }

  .history-timeline {
    padding: 12px 16px 16px;
  }

  .history-timeline__item {
    gap: 12px;
  }

  .history-timeline__separator {
    display: none;
  }

  .history-timeline__values {
    align-items: flex-start;
    flex-direction: column;
    gap: 4px;
  }

  .history-timeline__arrow {
    display: none;
  }
}

@media (max-width: 600px) {
  .history-dialog {
    /deep/ .md-dialog-container {
      width: calc(100vw - 12px);
      max-width: calc(100vw - 12px);
      margin: 6px;
    }

    /deep/ .md-dialog-title {
      font-size: 1.2rem;
    }
  }

  .history-dialog__content {
    min-width: 0;
  }

  .history-dialog__subtitle {
    font-size: 1.08rem;
  }

  .history-timeline__meta,
  .history-timeline__change,
  .history-timeline__value {
    font-size: 1.08rem;
  }

  .history-timeline__message {
    font-size: 1.14rem;
  }

  .history-timeline__field,
  .history-timeline__value-label {
    font-size: 1rem;
  }
}

.singleMaterialRow {
  text-align: center;
  padding-top: 25px;
  padding-bottom: 15px;
  border-bottom: 2px solid #7d7d7d;

  @include respond(small) {
    cursor: pointer;
  }
}

.blue-color {
  color: #313b8e;
  font-weight: 500;
}

.singleMaterialRow__data {
  font-size: 1.4rem;
}

.singleMaterialRow:hover {
  cursor: pointer;
}

.sticky_header {
  position: sticky;
  top: 0;
  background-color: white;
  z-index: 9;
}

.material-table {
  border: 1px solid #b90b0b;
  height: 98vh;
  overflow: scroll;

  .send-to-queue-btn {
    width: 100%;

    @include respond(small) {
      margin-top: 2vh;
    }
  }
}

.check-btn {
  padding-left: 4rem;
  padding-top: 0.8rem;

  @include respond(small) {
    padding-left: 1.3rem;
  }

  .input-check-box {
    transform: scale(2.1);

    @include respond(small) {
      transform: scale(1.6);
    }
  }
}

.mobile-remove {
  @include respond(small) {
    display: none;
  }
}

.singleMaterialRow__btns {
  border-top: 1px solid gray;
  padding-top: 15px;
  padding-bottom: 15px;
  border-bottom: 2px solid gray;
}

.main-icons {
  margin-left: 35px;

  @include respond(small) {
    margin-left: 15px;
  }
}

.expand-btn {
  cursor: pointer;
}

.expanded {
  background-color: #f1f1f1;
}

.icons {
  cursor: pointer;
  margin-left: 45px;
  font-size: 45px !important;

  @include respond(small) {
    font-size: 45px !important;
    margin-left: 45px;
  }
}

.material-table {
  margin-top: 40px;
}

.status_name {
  @include respond(small) {
    text-align: center;
  }
}

.close-modal-button {
  margin-bottom: 20px;
}

.materials-select__field {
  height: 30px;
  border-radius: 20px;
  border: 2px solid;
}

.singleMaterialRow__btns {
  .md-layout-item {
    padding-left: 40px;
  }
}

.material-hdrs {
  cursor: pointer;
}

.empty-table-message {
  text-align: center;
  padding: 40px 0;
  font-size: 1.6rem;
  color: #b90b0b;
  font-weight: bold;
}
</style>
